import { GaPageViewEvent, ViewportType } from '../types';
import { normalizePagePath } from './normalizePath';

export type PageViewProfile = {
  accountId?: number | null;
  /** 'person' or 'organisation'. Customer level, not per account. */
  customerType?: string | null;
  customerProfileType?: string | null;
  hasRedelivery?: boolean;
  productTypes?: string[] | null;
};

export type PageViewInput = {
  clientId?: string | null;
  clientIdEncoded?: string;
  isAuthenticated: boolean;
  isSpa: boolean;
  pageLocation: string;
  pageReferrer: string;
  pageRelease?: string;
  pageType?: string;
  platformRelease?: string;
  profile?: PageViewProfile | null;
  viewportType: ViewportType;
};

/**
 * Single source of truth for the page_view payload. Kept pure so both the full
 * page load and the SPA navigation path produce byte-identical shapes.
 *
 * `environment` is deliberately not set here: GTMProvider appends it to every
 * push, and setting it twice would be a silent divergence risk.
 *
 * Keys whose value is unknown are present with an explicit `undefined` rather
 * than omitted. Do not "optimise" this into conditional spreads: the GTM
 * dataLayer is a persistent merged model, so pushing `undefined` clears a value
 * set earlier in the session, whereas omitting the key leaves the stale one
 * readable by GTM variables — which would leak the previous customer's
 * `client_id` / `account_id` into every event after a logout.
 */
export const buildPageViewEvent = ({
  clientId,
  clientIdEncoded,
  isAuthenticated,
  isSpa,
  pageLocation,
  pageReferrer,
  pageRelease,
  pageType,
  platformRelease,
  profile,
  viewportType,
}: PageViewInput): GaPageViewEvent => ({
  event: 'page_view',
  account_id: profile?.accountId ?? undefined,
  client_id: isAuthenticated && clientId ? `${clientId}` : undefined,
  client_id_encoded: isAuthenticated ? clientIdEncoded : undefined,
  customer_profile_type: profile?.customerProfileType ?? undefined,
  customer_type: profile?.customerType ?? undefined,
  has_redelivery: profile?.hasRedelivery,
  is_spa: isSpa,
  login_status: isAuthenticated ? 'customer' : 'guest',
  page_location: pageLocation,
  page_path: normalizePagePath(pageLocation),
  page_referrer: pageReferrer,
  page_release: pageRelease,
  page_type: pageType,
  platform_release: platformRelease,
  // A comma-separated string, not an array: an array would need parsing in GTM.
  product_types: profile?.productTypes?.length ? profile.productTypes.join(',') : undefined,
  viewport_type: viewportType,
});
